/**
 * Product Page Upsells Handler
 * 
 * IMPORTANT: This file ONLY handles visual state (toggle ON/OFF, selection tracking)
 * - Toggle ON → Adds visual selection (blue border, checked state)
 * - Toggle OFF → Removes visual selection
 * - NO API calls to add/remove from cart immediately!
 * 
 * The actual cart addition happens in product-form-upsells.js when user clicks
 * the main "Add to Cart" button - it reads which upsells are selected and adds
 * them all together with the main product in one API call.
 * 
 * Handles:
 * - Toggle switches (ON/OFF visual state only)
 * - Checkboxes (checked/unchecked visual state only)
 * - Buttons (marks as selected when clicked, doesn't add to cart)
 * - Preselection (auto-check toggles on page load)
 * - Variant changes (updates variant ID in data attributes)
 */

(function() {
  'use strict';

  // Initialize when document is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    console.log('[Product Upsells] Initializing...');
    handlePreselectedUpsells();
    attachToggleListeners();
    attachVariantListeners();
    attachFormListeners();
  }

  /**
   * Handle preselected upsells when page loads
   * Just check the toggle - don't add to cart yet
   */
  function handlePreselectedUpsells() {
    const upsells = document.querySelectorAll('[data-upsell-preselected="true"]');
    
    upsells.forEach(upsell => {
      const toggle = upsell.querySelector('.product-upsell-toggle, .product-upsell-checkbox');
      const handle = upsell.dataset.upsellHandle;
      
      if (!toggle || toggle.disabled) return;

      // Check if product is already in cart
      checkIfInCart(handle).then(isInCart => {
        if (!isInCart && !toggle.checked) {
          console.log('[Product Upsells] Pre-selecting upsell (will add with main product)');
          toggle.checked = true;
          upsell.classList.add('product-upsell--selected');
        }
      });
    });
  }

  /**
   * Attach event listeners to toggles and checkboxes
   * NOTE: For product page, toggles just change state - they don't add to cart immediately
   * Items are added when main "Add to Cart" button is clicked (via product-form-upsells.js)
   */
  function attachToggleListeners() {
    // ONLY attach to product page upsells, NOT cart drawer upsells
    const productPageUpsells = document.querySelectorAll('.product-upsell-card .product-upsell-toggle, .product-upsell-card .product-upsell-checkbox, .compact-product-upsell .product-upsell-toggle, .compact-product-upsell .product-upsell-checkbox');
    
    console.log('[Product Upsells] Found', productPageUpsells.length, 'product page upsells to attach listeners');
    
    productPageUpsells.forEach(toggle => {
      toggle.addEventListener('change', function(e) {
        // Try multiple selectors for different upsell types
        const card = this.closest('.product-upsell-card') || 
                     this.closest('.compact-product-upsell') ||
                     this.closest('.product-upsell-inline') ||
                     this.closest('[data-upsell-variant-id]');
        
        if (!card) {
          console.warn('[Product Upsells] Could not find upsell card container');
          return;
        }
        
        // CRITICAL: Prevent any other handlers from running
        e.stopPropagation();
        
        // Just log the state change - don't add to cart yet
        if (this.checked) {
          console.log('[Product Upsells] Selected (will add with main product) - NO API CALL');
          card.classList.add('product-upsell--selected');
        } else {
          console.log('[Product Upsells] Deselected - NO API CALL');
          card.classList.remove('product-upsell--selected');
        }
      });
    });
  }

  /**
   * Attach event listeners to variant selects
   */
  function attachVariantListeners() {
    const variantSelects = document.querySelectorAll('.product-upsell-variant-select');
    
    variantSelects.forEach(select => {
      select.addEventListener('change', function() {
        const card = this.closest('.product-upsell-card');
        const variantSelects = card.querySelector('variant-selects');
        
        if (!variantSelects) return;

        // Get all selected options
        const selects = variantSelects.querySelectorAll('.product-upsell-variant-select');
        const selectedOptions = Array.from(selects).map(s => s.value);
        
        // Find matching variant
        const variantsData = JSON.parse(variantSelects.querySelector('[data-variants]').textContent);
        const matchingVariant = variantsData.find(variant => {
          return variant.options.every((option, index) => option === selectedOptions[index]);
        });

        if (matchingVariant) {
          console.log('[Product Upsells] Variant changed to:', matchingVariant.id);
          
          // Update card data attribute
          card.dataset.upsellVariantId = matchingVariant.id;
          
          // Update hidden input if button style
          const hiddenInput = card.querySelector('.product-upsell-variant-id');
          if (hiddenInput) {
            hiddenInput.value = matchingVariant.id;
          }

          // Update availability
          const button = card.querySelector('.product-upsell-button');
          const toggle = card.querySelector('.product-upsell-toggle, .product-upsell-checkbox');
          
          if (!matchingVariant.available) {
            if (button) {
              button.disabled = true;
              button.querySelector('.product-upsell-button__text').textContent = 'Sold Out';
            }
            if (toggle) {
              toggle.disabled = true;
              toggle.checked = false;
            }
          } else {
            if (button) {
              button.disabled = false;
              const originalLabel = card.querySelector('.product-upsell-button').dataset.originalLabel || 'Add to Cart';
              button.querySelector('.product-upsell-button__text').textContent = originalLabel;
            }
            if (toggle) {
              toggle.disabled = false;
            }
          }
        }
      });
    });
  }

  /**
   * Attach event listeners to form submissions (button style)
   * NOTE: For product pages, buttons DON'T add to cart immediately
   * They just toggle selection state, items are added when main "Add to Cart" is clicked
   */
  function attachFormListeners() {
    const forms = document.querySelectorAll('.product-upsell-form form');
    
    forms.forEach(form => {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        
        const card = this.closest('.product-upsell-card');
        const toggle = card.querySelector('.product-upsell-toggle, .product-upsell-checkbox');
        
        // Instead of adding to cart immediately, just mark as selected
        // The product-form-upsells.js will handle adding when main form submits
        if (toggle) {
          if (!toggle.checked) {
            console.log('[Product Upsells] Button clicked - marking as selected (will add with main product)');
            toggle.checked = true;
            card.classList.add('product-upsell--selected');
            // Trigger change event for any listeners
            toggle.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            console.log('[Product Upsells] Already selected');
          }
        } else {
          console.warn('[Product Upsells] No toggle found for button-style upsell');
        }
      });
    });
  }

  /**
   * Add product to cart
   * 
   * @deprecated This function is NO LONGER USED for product page upsells
   * Product page upsells are now added via product-form-upsells.js when
   * the main "Add to Cart" button is clicked.
   * 
   * Keeping this function for backwards compatibility / future use
   */
  function addToCart(variantId, card) {
    showLoading(card, true);
    
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: {
          _upsell: 'true'
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log('[Product Upsells] Added to cart successfully:', data);
      
      // Show success
      card.classList.add('added-to-cart');
      setTimeout(() => card.classList.remove('added-to-cart'), 3000);
      
      // Update cart count
      updateCartCount();
      
      // Open cart drawer (if drawer type cart)
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        // Open drawer
        cartDrawer.classList.add('is-active');
        document.body.classList.add('overflow-hidden');
      }
      
      showLoading(card, false);
      
      // Show notification (if you have a notification system)
      showNotification('Product added to cart!', 'success');
    })
    .catch(error => {
      console.error('[Product Upsells] Error adding to cart:', error);
      showLoading(card, false);
      
      // Uncheck toggle/checkbox
      const toggle = card.querySelector('.product-upsell-toggle, .product-upsell-checkbox');
      if (toggle) toggle.checked = false;
      
      // Show error
      card.classList.add('error');
      setTimeout(() => card.classList.remove('error'), 3000);
      
      showNotification('Error adding product to cart', 'error');
    });
  }

  /**
   * Remove product from cart by handle
   */
  function removeFromCart(handle, card) {
    showLoading(card, true);
    
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        // Find item by product handle
        const item = cart.items.find(i => 
          i.product.handle === handle && 
          i.properties && 
          i.properties._upsell === 'true'
        );
        
        if (!item) {
          console.log('[Product Upsells] Product not found in cart');
          showLoading(card, false);
          return;
        }

        // Update quantity to 0 to remove
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: item.key,
            quantity: 0
          })
        });
      })
      .then(response => {
        if (!response) return;
        return response.json();
      })
      .then(data => {
        if (!data) return;
        console.log('[Product Upsells] Removed from cart successfully');
        
        // Update cart count
        updateCartCount();
        
        // Refresh cart drawer if it exists
        if (typeof window.CartDrawer !== 'undefined') {
          window.CartDrawer.refresh();
        }
        
        showLoading(card, false);
        showNotification('Product removed from cart', 'info');
      })
      .catch(error => {
        console.error('[Product Upsells] Error removing from cart:', error);
        showLoading(card, false);
      });
  }

  /**
   * Check if product is already in cart
   */
  function checkIfInCart(handle) {
    return fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        return cart.items.some(item => item.product.handle === handle);
      })
      .catch(() => false);
  }

  /**
   * Show/hide loading state
   */
  function showLoading(card, show) {
    const spinner = card.querySelector('.loading-overlay__spinner, .product-upsell-spinner');
    const button = card.querySelector('.product-upsell-button');
    
    if (show) {
      card.classList.add('is-loading');
      if (spinner) spinner.classList.remove('hidden');
      if (button) button.disabled = true;
    } else {
      card.classList.remove('is-loading');
      if (spinner) spinner.classList.add('hidden');
      if (button && card.querySelector('.product-upsell-toggle, .product-upsell-checkbox')?.disabled === false) {
        button.disabled = false;
      }
    }
  }

  /**
   * Update cart count in header
   */
  function updateCartCount() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const countElements = document.querySelectorAll('.cart-count-bubble span, [data-cart-count]');
        countElements.forEach(el => {
          el.textContent = cart.item_count;
        });
      });
  }

  /**
   * Show notification (basic implementation)
   */
  function showNotification(message, type = 'info') {
    // Check if theme has notification system
    if (typeof window.showNotification === 'function') {
      window.showNotification(message, type);
      return;
    }

    // Fallback: console log
    console.log(`[Notification] ${type.toUpperCase()}: ${message}`);
    
    // Optional: You can create a simple toast notification here
  }

  // Expose for external use
  // NOTE: addToCart and removeFromCart are NOT exposed anymore
  // Product page upsells should use product-form-upsells.js instead
  window.ProductUpsells = {
    init: init,
    handlePreselectedUpsells: handlePreselectedUpsells
    // addToCart: removed - deprecated for product pages
    // removeFromCart: removed - deprecated for product pages
  };

})();

